<template>
  <entity-edit-dialog
      @submit="submit"
      @close="close"
      v-model="visible"
      ref="editDialog"
      :dialog-title="dialogTitle"
      :submitting="submitting">
    <template v-slot:contentCard>
      <v-card-text class="pt-5">
        <entity-dialog-input-row :label="$t('plannedService.name')" :required="true">
          <v-text-field
              v-model="plannedService.name"
              ref="nameInput"
              outlined
              hide-details="auto"
              rows="2"
              style="max-width: 20em"
              :rules="[(v) => !!v || $t('validation.required'),]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.device')" :required="true">
          <device-select-field :show-add-dialog-button="true"
                               :show-select-dialog-button="true"
                               v-model="plannedService.device"
                               :rules="[(v) => !!v || $t('validation.required'),]"/>

        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.priority')">
          <priority-select v-model="plannedService.priority"
                           :rules="[(v) => !!v || $t('validation.required'),]"/>
        </entity-dialog-input-row>


        <entity-dialog-input-row :label="$t('plannedService.schedulingType')" divider-before>
          <v-radio-group v-model="repeatType"
                         :hide-details="true"
                         @change="repeatTypeChanged">
            <v-radio :label="$t('plannedService.singleService')" value="single"/>
            <v-radio :label="$t('plannedService.repeatingService')" value="repeating"/>
          </v-radio-group>
        </entity-dialog-input-row>

        <entity-dialog-input-row v-if="repeatType === 'single' "
                                 :label="$t('plannedService.scheduleDate')"
                                 :required="true">
          <date-picker v-model="plannedService.startDate"
                       :rules="[(v) =>(!!v || repeatType === 'repeating') || $t('validation.required'),]"/>
        </entity-dialog-input-row>


        <entity-dialog-input-row v-if="isRepeating"
                                 :label="$t('plannedService.repeatStartDate')"
                                 :required="true">
          <date-picker v-model="plannedService.startDate"
                       :rules="[requiredOnRepeatingValidator,]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row v-if="isRepeating"
                                 :label="$t('plannedService.repeatEndDate')"
                                 :required="true">
          <date-picker v-model="plannedService.repeatEndDate"
                       :rules="[requiredOnRepeatingValidator,]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row v-if="isRepeating"
                                 :label="$t('plannedService.repetition')" :required="true">
          <div class="d-flex flex-column">
            <div class="d-inline-flex flex-row">
              <v-text-field
                  type="number"
                  min="0"
                  outlined
                  class="shrink mr-3 right-align-number-input"
                  hide-details="auto"
                  v-model="plannedService.repeatCount"
                  style="width: 6em;
                    text-align: right"
                  :rules="[requiredOnRepeatingValidator,]"/>
              <v-select
                  v-model="plannedService.repeatType"
                  outlined
                  :items="repeatTypes"
                  hide-details="auto"
                  style="max-width: 15em"
                  :rules="[requiredOnRepeatingValidator,]"/>
            </div>
            <div v-if="plannedService.repeatType === 'WEEK'"
                 class="d-inline-flex flex-row flex-wrap repeat-weekdays">
              <v-checkbox v-model="plannedService.weekRepeatDayMonday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.monday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDayTuesday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.tuesday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDayWednesday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.wednesday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDayThursday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.thursday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDayFriday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.friday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDaySaturday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.saturday')"/>
              <v-checkbox v-model="plannedService.weekRepeatDaySunday" dense hide-details
                          :label="$t('plannedService.repeatDaysOfWeek.sunday')"/>
            </div>
          </div>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.duration')" divider-before>
          <duration-select v-model="plannedService.durationMinutes" style="max-width: 10em"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.description')" :required="true">
          <v-textarea
              v-model="plannedService.description"
              rows="3"
              outlined
              hide-details="auto"
              :rules="[(v) =>!!v || $t('validation.required'),]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.requiredParts')">
          <part-quantities-select v-model="plannedService.requiredParts"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.documents')">
          <documents-select v-model="documents"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.serviceManagers')" divider-before>
          <multiple-users-select v-model="plannedService.serviceManagers"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('plannedService.authorUsers')">
          <multiple-users-select v-model="plannedService.authorUsers"/>
        </entity-dialog-input-row>
      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import DatePicker from '../../components/inputs/DatePicker.vue';
import DurationSelect from '../../components/inputs/DurationSelect.vue';
import EntityDialogInputRow from '@/components/entity-crud/EntyDialogInputRow';
import DeviceSelectField from '@/components/inputs/DeviceSelectField';
import PartQuantitiesSelect from '@/components/inputs/PartQuantitiesSelect';
import MultipleUsersSelect from '@/components/inputs/MultipleUsersSelect';
import i18n from '@/i18n';
import PrioritySelect from '@/components/inputs/PrioritySelect';
import PlannedServiceService from '@/services/PlannedServiceService';
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import DocumentsSelect from '@/components/inputs/DocumentsSelect';

export default {
  name: 'PlannedServiceDialog',
  components: {
    DocumentsSelect,
    EntityEditDialog,
    PrioritySelect,
    MultipleUsersSelect,
    PartQuantitiesSelect,
    DeviceSelectField,
    EntityDialogInputRow,
    DatePicker,
    DurationSelect,
  },
  data: () => ({
    plannedService: null,
    documents: [],
    visible: false,
    submitting: false,
    repeatType: 'single',
  }),
  props: {},
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      if (this.plannedService?.id) {
        return i18n.t('plannedService.editDialog.titleEdit');
      } else {
        return i18n.t('plannedService.editDialog.titleAdd');
      }
    },

    isRepeating() {
      return this.repeatType === 'repeating';
    },

    requiredOnRepeatingValidator() {
      return (v) => (this.repeatType !== 'repeating' || !!v) || i18n.t('validation.required');
    },

    repeatTypes() {
      return [
        {
          text: this.$t('plannedService.repeatTypeDAY'),
          value: 'DAY',
        },
        {
          text: this.$t('plannedService.repeatTypeWEEK'),
          value: 'WEEK',
        },
        {
          text: this.$t('plannedService.repeatTypeMONTH'),
          value: 'MONTH',
        },
      ];
    },
  },
  methods: {
    close() {
      this.plannedService = null;
      this.visible = false;
      this.submitting = false;
    },

    openCreateNewPlannedService() {
      this.plannedService = {
        name: null,
        device: null,
        description: null,
        priority: 2,
        requiredParts: [],
        serviceManagers: [],
        authorUsers: [],
        locationTop: null,
        locationSub: null,
        startDate: null,
        repeatEndDate: null,
        repeatCount: 1,
        repeatType: 'DAY',
        weekRepeatDayMonday: false,
        weekRepeatDayTuesday: false,
        weekRepeatDayWednesday: false,
        weekRepeatDayThursday: false,
        weekRepeatDayFriday: false,
        weekRepeatDaySaturday: false,
        weekRepeatDaySunday: false,
        durationMinutes: null,
      };
      this.documents = [];
      this.repeatType = 'single';
      this.visible = true;
    },

    openEditForPlannedService(plannedServiceId) {
      PlannedServiceService.get(plannedServiceId)
          .then(result => {
            this.plannedService = result.data.plannedService;
            this.documents = result.data.documents;
            if (this.plannedService.repeatEndDate) {
              this.repeatType = 'repeating';
            } else {
              this.repeatType = 'single';
            }

            this.visible = true;
          }).catch(err => {
        console.log(JSON.stringify(err));
      });
    },


    submit() {
      if (this.submitting) {
        return;
      }
      if (!this.$refs.editDialog.$refs.form.validate()) {
        this.$refs.editDialog.showFormHasValidationErrorsMessage(i18n.t('plannedService.requiredInformationMissing'));
        return;
      }

      let plannedServiceDTO = {
        plannedService: this.repetitionRuleCleanup(this.plannedService),
        documents: this.documents,
      };

      this.submitting = true;
      PlannedServiceService.save(plannedServiceDTO).then((result) => {
        this.$emit('done', result.data);
        this.close();
      }).catch(err => {
        console.log(JSON.stringify(err));
      }).finally(() => {
        this.submitting = false;
      });
    },
    repetitionRuleCleanup(plannedService) {
      if (this.repeatType === 'repeating') {
        return plannedService;
      }
      plannedService = JSON.parse(JSON.stringify(plannedService));
      plannedService.repeatEndDate = null;
      plannedService.repeatCount = null;
      plannedService.repeatType = null;
      return plannedService;
    },

    repeatTypeChanged() {
      if (this.repeatType !== 'repeating') {
        return;
      }
      if (!this.plannedService.repeatCount) {
        this.plannedService.repeatCount = 1;
      }
      if (!this.plannedService.repeatType) {
        this.plannedService.repeatType = 'DAY';
      }
    },
  },
};
</script>

<style scoped>
.repeat-weekdays > * {
  margin-right: 1em;
}
</style>
